import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCText } from "../lib/cms";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/seo";

const EMPTY = { name: "", email: "", phone: "", location: "", quantity: "", genres: "", message: "", website: "" };

export default function SellRecords() {
  const { lang } = useI18n();
  const tx = (es, en) => (lang === "en" ? en : es);
  const title = useCText("sell.title", "sell.title");
  const lead = useCText("sell.lead", "sell.lead");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  useSEO({ title: tx("Compramos tus discos · Maggie Records", "We buy your records · Maggie Records"), description: lead });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/sell-request", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tx("No se pudo enviar. Inténtalo de nuevo.", "Couldn't send. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    [tx("Nos cuentas", "Tell us"), tx("Qué discos tienes, cuántos y en qué estado. Si puedes, una lista o fotos de los lomos.", "What records, how many and their condition. A list or photos of the spines help.")],
    [tx("Te hacemos oferta", "We make an offer"), tx("Revisamos la colección y te proponemos un precio, sin compromiso.", "We review the collection and propose a price, no strings attached.")],
    [tx("Cerramos el trato", "We close the deal"), tx("Si te encaja, acordamos contigo el pago y la entrega o recogida.", "If it works for you, we agree on payment and delivery or pick-up.")],
  ];

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-10 md:pt-14" data-testid="sell-page">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <h1 className="text-2xl md:text-4xl font-medium leading-tight mb-5">{title}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8 max-w-md">{lead}</p>
          <ol className="space-y-5 max-w-md">
            {steps.map(([h, p], i) => (
              <li key={h} className="flex gap-4">
                <span className="vmr-tiny tabular-nums text-muted-foreground mt-0.5">0{i + 1}</span>
                <span>
                  <span className="block text-sm font-medium">{h}</span>
                  <span className="block text-sm text-muted-foreground leading-relaxed">{p}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="md:col-span-7">
          {sent ? (
            <div className="border border-border p-8 max-w-xl" data-testid="sell-success">
              <h2 className="text-xl font-medium mb-2">{tx("¡Recibido!", "Received!")}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {tx("Gracias. Revisamos tu mensaje y te contestamos en menos de 48 h.", "Thanks. We'll review your message and reply within 48 h.")}
              </p>
              <button onClick={() => setSent(false)} className="mt-6 text-xs underline underline-offset-4 text-muted-foreground hover:text-foreground">
                {tx("Enviar otra oferta", "Send another offer")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4 max-w-xl" data-testid="sell-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={tx("Nombre", "Name")} value={form.name} onChange={set("name")} required testid="sell-name" />
                <Field label="Email" type="email" value={form.email} onChange={set("email")} required testid="sell-email" />
                <Field label={tx("Teléfono / WhatsApp (opcional)", "Phone / WhatsApp (optional)")} type="tel" value={form.phone} onChange={set("phone")} placeholder="+34 600 000 000" testid="sell-phone" />
                <Field label={tx("Ciudad / país", "City / country")} value={form.location} onChange={set("location")} testid="sell-location" />
                <Field label={tx("Nº aproximado de discos", "Approx. number of records")} value={form.quantity} onChange={set("quantity")} placeholder={tx("ej. 200", "e.g. 200")} testid="sell-quantity" />
                <Field label={tx("Géneros principales", "Main genres")} value={form.genres} onChange={set("genres")} placeholder={tx("techno, house, jazz…", "techno, house, jazz…")} testid="sell-genres" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-muted-foreground" htmlFor="sell-message">
                  {tx("Cuéntanos qué tienes (artistas, estado, si hay lista o fotos…)", "Tell us what you have (artists, condition, list or photos…)")}
                </label>
                <textarea
                  id="sell-message"
                  value={form.message}
                  onChange={(e) => set("message")(e.target.value)}
                  rows={6}
                  required
                  className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
                  data-testid="sell-message"
                />
              </div>
              {/* Honeypot for bots: hidden from people and screen readers. */}
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" />
              <button
                type="submit"
                disabled={loading}
                className="self-start bg-foreground text-background px-6 py-2.5 text-sm hover:bg-[hsl(var(--accent))] transition-colors disabled:opacity-50"
                data-testid="sell-submit"
              >
                {loading ? tx("Enviando…", "Sending…") : tx("Enviar oferta", "Send offer")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs text-muted-foreground" htmlFor={testid}>{label}</label>
      <input
        id={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
        data-testid={testid}
      />
    </div>
  );
}
